import {tokens} from '@pleo-io/telescope'
import VisuallyHidden from '@reach/visually-hidden'
import {graphql} from 'gatsby'
import React, {FC, useState} from 'react'
import styled from 'styled-components'

import {PricingFragment} from '@/graphql-types'
import {useEmailValidationErrorMessage} from '@/hooks/use-email-validation-error-message'
import useSignup from '@/hooks/use-signup'
import {wait} from '@/utils/wait'
import Button from '@helios/shared/components/button'
import Input from '@helios/shared/components/input'
import {Modal, ModalContent, ModalActions} from '@helios/shared/components/modal'
import RichText, {Props as RichTextProps} from '@helios/shared/components/rich-text'
import useNavigate from '@helios/shared/hooks/use-navigate'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {usePageInfo} from '@helios/shared/providers/page-info'
import {text, Text} from '@helios/shared/styles/typography'
import {colors} from '@helios/shared/theme'
import ImageSource from '@helios/shared/types/image'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'
import mq from '@helios/shared/utils/media-query'

import * as s from './pricing.styles'

export interface Props {
    withCtas?: boolean
}

interface PricingProps extends Props {
    content: PricingFragment['data']
    mode: BillingCycle
}

enum Tier {
    Starter = 'starter',
    Essential = 'essential',
    Advanced = 'advanced',
}

export enum BillingCycle {
    Monthly = 'monthly',
    Yearly = 'yearly',
}

export const Pricing: FC<PricingProps> = ({
    content,
    mode = BillingCycle.Monthly,
    withCtas = true,
}) => {
    const [activeItemIndex, setActiveItemIndex] = useState(0)
    const [activeSignupModal, setActiveSignupModal] = React.useState<Tier | null>(null)
    const [activeAdditionalUsersModal, setActiveAdditionalUsersModal] = useState(false)
    const tiers = content.pricing_tiers.map(({pricing_tier}) => pricing_tier.document.data)
    const navigate = useNavigate()
    const {location} = usePageInfo()

    const getTierName = (index: number): Tier => {
        switch (index) {
            case 0:
                return Tier.Starter

            case 1:
                return Tier.Essential

            case 2:
                return Tier.Advanced

            default:
                throw new Error('Unrecognized tier')
        }
    }

    const handleAdvancedTierClick = async () => {
        track(TrackingEvent.PlanSelected, {
            value: Tier.Advanced,
        })

        await wait(300)

        navigate('/demo')
    }

    const handleAdditionalUsersModalOpen = () => {
        track(TrackingEvent.PricingAdditionalUsersModalOpened, {action: 'opened'})

        setActiveAdditionalUsersModal(true)
    }

    React.useEffect(() => {
        if (location.hash === '#additional-users') {
            handleAdditionalUsersModalOpen()
        }
    }, [location])

    return (
        <s.OuterWrapper>
            <s.Wrapper activeItemIndex={activeItemIndex}>
                <s.Tabs>
                    {tiers.map((tier, i) => (
                        <s.Tab
                            key={tier.name}
                            isActive={i === activeItemIndex}
                            onClick={() => setActiveItemIndex(i)}
                        >
                            <Text as="span" center>
                                {tier.name}
                            </Text>
                        </s.Tab>
                    ))}
                </s.Tabs>

                <s.Title>
                    {tiers.map((tier) => (
                        <s.TiersTitle key={tier.name}>
                            <Text as="h3" size="lg">
                                {tier.name}
                            </Text>
                        </s.TiersTitle>
                    ))}
                </s.Title>

                <s.Amount withCtas={withCtas}>
                    {tiers.map((tier) => {
                        const previousCost =
                            mode === BillingCycle.Monthly
                                ? tier.previous_cost_per_month
                                : tier.previous_cost_per_year

                        const newCost =
                            mode === BillingCycle.Monthly ? tier.cost_per_month : tier.cost_per_year

                        const costText =
                            mode === BillingCycle.Monthly ? tier.per_month_text : tier.per_year_text

                        return (
                            <s.Box key={tier.name}>
                                <s.AmountText color="muted" variant="paragraph-large">
                                    <del>
                                        <VisuallyHidden>
                                            {tier.old_cost_screen_reader_alt}
                                        </VisuallyHidden>
                                        {previousCost}
                                    </del>{' '}
                                    <ins>
                                        <VisuallyHidden>
                                            {tier.new_cost_screen_reader_alt}
                                        </VisuallyHidden>
                                        {newCost}
                                    </ins>{' '}
                                    {costText}
                                </s.AmountText>
                                <div>
                                    <Text size="xs" font="secondary" as="span">
                                        {tier.cost_subtext}
                                    </Text>{' '}
                                    {tier.open_additional_users_modal_text && (
                                        <s.AdditionalUsersModalText
                                            size="xs"
                                            font="secondary"
                                            as="button"
                                            onClick={handleAdditionalUsersModalOpen}
                                            data-testid="additional-users-modal-button"
                                        >
                                            {tier.open_additional_users_modal_text}
                                        </s.AdditionalUsersModalText>
                                    )}
                                </div>
                            </s.Box>
                        )
                    })}
                </s.Amount>

                <s.Excerpt>
                    {tiers.map((tier) => (
                        <s.Box key={tier.name}>
                            <s.ExcerptText variant="paragraph-base">{tier.excerpt}</s.ExcerptText>
                        </s.Box>
                    ))}
                </s.Excerpt>

                {withCtas && (
                    <s.Cta>
                        {tiers.map((tier, i) => (
                            <s.Box key={tier.name}>
                                {i === tiers.length - 1 ? (
                                    <Button
                                        data-testid={`pricing-tier-cta-${tier.name}`}
                                        onClick={handleAdvancedTierClick}
                                        variant={!(i % 2) ? 'outline' : 'default'}
                                    >
                                        {tier.cta_text}
                                    </Button>
                                ) : (
                                    <Button
                                        data-testid={`pricing-tier-cta-${tier.name}`}
                                        onClick={() => {
                                            track(TrackingEvent.PlanSelected, {
                                                value: getTierName(i),
                                            })

                                            setActiveSignupModal(getTierName(i))
                                        }}
                                        variant={!(i % 2) ? 'outline' : 'default'}
                                    >
                                        {tier.cta_text}
                                    </Button>
                                )}
                            </s.Box>
                        ))}
                    </s.Cta>
                )}

                {hasNonEmptyItems(content.signup_modal_free) && (
                    <SignupModal
                        {...content.signup_modal_free[0]}
                        isOpen={activeSignupModal === Tier.Starter}
                        onDismiss={() => setActiveSignupModal(null)}
                        tier={Tier.Starter}
                    />
                )}

                {hasNonEmptyItems(content.signup_modal_pro) && (
                    <SignupModal
                        {...content.signup_modal_pro[0]}
                        isOpen={activeSignupModal === Tier.Essential}
                        onDismiss={() => setActiveSignupModal(null)}
                        tier={Tier.Essential}
                    />
                )}

                {hasNonEmptyItems(content.additional_users_modal) && (
                    <AdditionalUsersModal
                        title={content.additional_users_modal[0].title1}
                        description={content.additional_users_modal[0].description.raw}
                        table={content.additional_users_modal[0].table.document.data}
                        isOpen={activeAdditionalUsersModal}
                        onDismiss={() => setActiveAdditionalUsersModal(false)}
                    />
                )}

                <s.FeaturesTitle>
                    {tiers.map((tier) => (
                        <s.Box key={tier.name}>
                            <Text>{tier.features_title}</Text>
                        </s.Box>
                    ))}
                </s.FeaturesTitle>

                <s.Row>
                    {tiers.map((tier) => (
                        <s.Box key={tier.name}>
                            <s.FeaturesList>
                                {tier.features.map(
                                    ({feature, disabled_monthly, is_highlighted}) => (
                                        <s.FeaturesItem
                                            $isHighlighted={
                                                is_highlighted ||
                                                (disabled_monthly && mode === BillingCycle.Yearly)
                                            }
                                            $isDisabled={
                                                disabled_monthly && mode === BillingCycle.Yearly
                                            }
                                            key={feature}
                                        >
                                            {feature}
                                        </s.FeaturesItem>
                                    )
                                )}
                            </s.FeaturesList>
                        </s.Box>
                    ))}
                </s.Row>
            </s.Wrapper>
        </s.OuterWrapper>
    )
}

type SignupModalProps = {
    isOpen: boolean
    onDismiss: () => void
    title: string
    subtitle: string
    image: ImageSource
    tier: Tier
}

const SignupModal = ({isOpen, onDismiss, title, subtitle, image, tier}: SignupModalProps) => {
    const globalStrings = useGlobalStrings()
    const {handleSubmit, clearError, setEmail, email, loading, error} = useSignup({
        form: 'Front',
        params: {pricing_plan: tier},
    })
    const errorMessage = useEmailValidationErrorMessage(error)

    const handleEmailChange = (value: string) => {
        clearError()
        setEmail(value)
    }

    const handleDismiss = () => {
        clearError()
        setEmail('')
        onDismiss()
    }

    return (
        <Modal
            title={title}
            subtitle={subtitle}
            image={image}
            isOpen={isOpen}
            onDismiss={handleDismiss}
        >
            <form onSubmit={handleSubmit} noValidate>
                <ModalContent>
                    <Input
                        type="email"
                        label={globalStrings.work_email}
                        value={email}
                        onChange={handleEmailChange}
                        error={errorMessage}
                        autoFocus
                        data-testid="modal-input"
                    />
                </ModalContent>

                <ModalActions>
                    <Button type="submit" loading={loading} data-testid="modal-submit-button">
                        {globalStrings.submit}
                    </Button>
                </ModalActions>
            </form>
        </Modal>
    )
}

type AdditionalUsersModalProps = {
    isOpen: boolean
    onDismiss: () => void
    title: string
    description: RichTextProps['content']
    table: AdditionalUsersTable
}

type AdditionalUsersRowType = {
    users_range: string
    essential_price: string
    advanced_price: string
}

type AdditionalUsersTable = {
    users_range_heading: string
    essential_price_heading: string
    advanced_price_heading: string
    rows: AdditionalUsersRowType[]
}

const AdditionalUsersModal = ({
    isOpen,
    onDismiss,
    title,
    description,
    table,
}: AdditionalUsersModalProps) => (
    <Modal title={title} isOpen={isOpen} onDismiss={onDismiss} dataTestId="additional-users-modal">
        <ModalContent>
            <RichText content={description} font="secondary" size="xxs" color="muted" />
            <TableWrapper>
                <Table>
                    <tr>
                        <th scope="col">{table.users_range_heading}</th>
                        <th scope="col">{table.essential_price_heading}</th>
                        <th scope="col">{table.advanced_price_heading}</th>
                    </tr>
                    {table.rows &&
                        table.rows.map((row) => (
                            <tr key={row.users_range}>
                                <th scope="row">{row.users_range}</th>
                                <td>{row.essential_price}</td>
                                <td>{row.advanced_price}</td>
                            </tr>
                        ))}
                </Table>
            </TableWrapper>
        </ModalContent>
    </Modal>
)

export const TableWrapper = styled.div`
    margin-top: ${tokens.spacing40};
    overflow-x: auto;
`

export const Table = styled.table`
    width: 100%;
    text-align: left;
    border-collapse: collapse;

    th,
    td {
        ${text({variant: 'label-base'})}
        padding-top: ${tokens.spacing16};
        padding-bottom: ${tokens.spacing16};

        ${mq.min('sm')} {
            ${text({variant: 'paragraph-base'})}
        }
    }

    th:not(:first-child),
    td:not(:first-child) {
        padding-left: ${tokens.spacing4};
        text-align: center;
    }

    th:not(:last-child),
    td:not(:last-child) {
        padding-right: ${tokens.spacing4};
    }

    tr:first-child th {
        padding-top: 0;
        padding-bottom: ${tokens.spacing20};
        word-break: normal;
        vertical-align: top;
    }

    tr + tr:not(:last-child) {
        border-bottom: 1px solid ${colors.border.default};
    }
`

export const pricingFragment = graphql`
    fragment Pricing on PrismicPricingTiers {
        lang
        data {
            billing_cycle {
                billed_monthly_label
                billed_yearly_label
                description
                legend
            }
            signup_modal_free {
                image {
                    alt
                    copyright
                    url
                    dimensions {
                        width
                        height
                    }
                }
                title
                subtitle
            }
            signup_modal_pro {
                image {
                    alt
                    copyright
                    url
                    dimensions {
                        width
                        height
                    }
                }
                title
                subtitle
            }
            additional_users_modal {
                title1
                description {
                    raw
                }
                table {
                    document {
                        ... on PrismicAdditionalUsersTable {
                            id
                            data {
                                users_range_heading
                                essential_price_heading
                                advanced_price_heading
                                rows {
                                    users_range
                                    essential_price
                                    advanced_price
                                }
                            }
                        }
                    }
                }
            }
            pricing_tiers {
                pricing_tier {
                    ... on PrismicLinkType {
                        document {
                            ... on PrismicPricingTier {
                                data {
                                    name
                                    cost_per_month
                                    previous_cost_per_month
                                    per_month_text
                                    cost_per_year
                                    previous_cost_per_year
                                    per_year_text
                                    cost_subtext
                                    old_cost_screen_reader_alt
                                    new_cost_screen_reader_alt
                                    open_additional_users_modal_text
                                    excerpt
                                    cta_text
                                    features_title
                                    features {
                                        feature
                                        disabled_monthly
                                        is_highlighted
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default Pricing
